import { Fragment, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Text } from 'components/Typography';
import MessageLayout from '../common/MessageLayout';
import { ReactComponent as IconBoxFlat } from 'constants/icon/icon_box_flat.svg';
import styled from 'styled-components';
import { set, useForm } from 'react-hook-form';
import { Stack, StackProps } from 'components/Stack';
import { ICustomerInfo, useCheckedCustomerInfos } from './ManualStore';
import ManualMessageComponent from './ManualMessageComponent';
import { Button } from 'components/Button';
import theme from 'constants/theme';
import { AlertModal } from 'components/Modal/AlertModal';
import { useIsDifferenceStore } from '../common/MessagePromptStore';
import useToastStore from 'components/Toast/ToastStore';
import RequestErrorModal from '../common/RequestErrorModal';
import { TRequestFilter } from './Manual';
import { postManualSend } from '../api';
import { useTransferRecipent } from '../History/HistoryStore';
import { WarningModal } from 'components/Modal/warning';

export interface IManualMessageInfo {
  title: string;
  contents: string;
  sendDate: string;
  sendHour: string;
  sendMinute: string;
  isSendNow: boolean;
  recipients: ICustomerInfo[];
}

type Props = {
  requestFilter: TRequestFilter;
};

const ManualMessage = ({ requestFilter }: Props) => {
  const { addToast } = useToastStore();
  const { isDifference, setIsDifference } = useIsDifferenceStore();
  const { checkedCustomerInfos, setCheckedCustomerInfos } = useCheckedCustomerInfos();
  const { transferRecipent, setTransferRecipent } = useTransferRecipent();
  const { register, handleSubmit, setValue, watch, reset } = useForm<IManualMessageInfo>({
    defaultValues: {
      title: '',
      contents: '',
      sendDate: '',
      sendHour: '00',
      sendMinute: '00',
      isSendNow: true,
      recipients: [],
    },
  });

  const [isOpenCancel, setIsOpenCancel] = useState(false); // 작성 취소 모달
  const [isOpenSend, setIsOpenSend] = useState(false); // 발송하기 모달
  const [isOpenErrorModal, setIsOpenErrorModal] = useState(false); // 메시지 발송 실패 모달
  const [isOpenTimeErrorModal, setIsOpenTimeErrorModal] = useState(false); // 발송 시간 오류 모달
  const [isOpen10MinErrorModal, setIsOpen10MinErrorModal] = useState(false); // 10분 이후 발송 모달

  // 저장하기 버튼 활성화 여부
  const isButtonDisabled = useMemo(() => {
    const { title, contents, sendDate, sendHour, sendMinute, recipients, isSendNow } = watch();
    if (title.length > 0 && contents.length > 0 && recipients.length > 0) {
      if (isSendNow) {
        return false;
      } else {
        if (sendDate.length > 0) {
          return false;
        }
      }
    }
    return true;
  }, [watch()]);

  // ManualTable.tsx 에서 데이터 refetch 시 초기화 처리
  useEffect(() => {
    setCheckedCustomerInfos([]);
    reset();
  }, [requestFilter]);

  // 작성 데이터 보내기 처리
  const { mutate } = useMutation({
    mutationFn: async (params: IManualMessageInfo) => {
      const requestParams = {
        ...params,
        sendDate: params.isSendNow ? dayjs().format('YYYY.MM.DD') : params.sendDate,
        sendHour: params.isSendNow
          ? dayjs().hour().toString().length === 1
            ? `0${dayjs().hour().toString()}`
            : dayjs().hour().toString()
          : params.sendHour,
        sendMinute: params.isSendNow
          ? dayjs().minute().toString().length === 1
            ? `0${dayjs().minute().toString()}`
            : dayjs().minute().toString()
          : params.sendMinute,
      };
      return postManualSend(requestParams);
    },
    onMutate: async () => {},
    onSuccess: async e => {
      if (!!e?.statusCode) {
        if (e?.statusCode.startsWith('4') || e?.statusCode.startsWith('5')) {
          //에러 모달 띄우기
          setIsOpenErrorModal(true);
          setIsOpenSend(false);
        } else {
          // 성공 토스트 띄우기 + 데이터 초기화
          setIsOpenSend(false);
          addToast(`${watch('isSendNow') ? '메시지가 발송되었습니다.' : '메시지가 발송 예약 되었습니다.'}`, 3000);
          setCheckedCustomerInfos([]);
          // transferRecipent 초기화
          setTransferRecipent(null);
          setIsDifference(false);
          reset();
        }
      } else {
        // 성공 토스트 띄우기 + 데이터 초기화
        setIsOpenSend(false);
        addToast(`${watch('isSendNow') ? '메시지가 발송되었습니다.' : '메시지가 발송 예약 되었습니다.'}`, 3000);
        setCheckedCustomerInfos([]);
        // transferRecipent 초기화
        setTransferRecipent(null);
        setIsDifference(false);
        reset();
      }
    },
    onError: async f => {
      //에러 모달 띄우기
      setIsOpenErrorModal(true);
      setIsOpenSend(false);
    },
  });

  // 체크박스로 선택된 고객 정보를 form state에 반영
  useEffect(() => {
    if (checkedCustomerInfos.length > 0) {
      // 고객 정보 추가시 임시작성 여부 모달 띄우기
      setIsDifference(true);
    }
    setValue('recipients', checkedCustomerInfos);
  }, [checkedCustomerInfos, setValue]);

  const onClickCancel = () => {
    setIsOpenCancel(true);
  };
  const onClickSend = () => {
    // 현재 시간보다 이전 시간인지 체크
    if (!watch('isSendNow')) {
      const sendDate = watch('sendDate');
      const sendHour = watch('sendHour');
      const sendMinute = watch('sendMinute');
      const sendDateTime = dayjs(`${sendDate} ${sendHour}:${sendMinute}`);
      // 현재 시간보다 이전 시간인 경우 모달 띄우기
      if (sendDateTime.isBefore(dayjs())) {
        setIsOpenTimeErrorModal(true);
        // 시간 초기화 처리
        setValue('sendDate', '');
        setValue('sendHour', '00');
        setValue('sendMinute', '00');
        return;
      }
      // 예역 발송 시간이 현재 시간 + 11분 이후인지 체크
      if (sendDateTime.isBefore(dayjs().add(11, 'minute'))) {
        setIsOpen10MinErrorModal(true);
        // 시간 초기화 처리
        setValue('sendDate', '');
        setValue('sendHour', '00');
        setValue('sendMinute', '00');
        return;
      }
    }
    setIsOpenSend(true);
  };

  const onClickModalWriteCancel = () => {
    setCheckedCustomerInfos([]);
    setIsOpenCancel(false);
    setIsDifference(false);
    reset();
  };
  const onClickModalWriteContinue = () => {
    setIsOpenCancel(false);
  };

  const onClickModalSendCancel = () => {
    setIsOpenSend(false);
  };
  const onClickModalSend = () => {
    mutate(watch());
  };

  return (
    <Fragment>
      <MessageLayout
        titleComponent={
          <TitleDiv>
            <IconBoxFlat width={20} height={20} />
            <Text
              styleName="headline1"
              color="RG02"
              sx={{
                fontSize: 18,
                fontFamily: 'Pretendard',
                fontWeight: 700,
              }}
            >
              메시지 보내기
            </Text>
          </TitleDiv>
        }
        messageComponent={<ManualMessageComponent register={register} watch={watch} setValue={setValue} />}
        buttonComponent={
          <ButtonDiv>
            {checkedCustomerInfos.length > 0 && (
              <CancelButtonStyle type="button" variant="secondary" h={32} disabled={false}>
                <ButtonText
                  disable={false}
                  style={{
                    color: theme.colors.RG03,
                    textAlign: 'center',
                    fontSize: 16,
                    fontWeight: 700,
                  }}
                  onClick={onClickCancel}
                >
                  취소하기
                </ButtonText>
              </CancelButtonStyle>
            )}

            <SaveButtonStyle type="button" variant="secondary" h={32} disabled={isButtonDisabled} onClick={onClickSend}>
              <ButtonText disable={isButtonDisabled}>발송하기</ButtonText>
            </SaveButtonStyle>
          </ButtonDiv>
        }
      />
      <AlertModal
        type="WARNING"
        isOpen={isOpenCancel}
        messages={
          <Stack sx={{ gap: '8px' }}>
            <Text styleName="body2" color="RG02">
              이전으로 돌아가시겠습니까?
            </Text>
            <Text styleName="body2" color="RG02">
              작성 중이던 내용은 저장되지 않습니다.
            </Text>
          </Stack>
        }
        buttonGroup={
          <ModalButtonDiv>
            <Button
              variant="tertiary"
              h={40}
              sx={{ width: '100%', marginTop: '40px' }}
              onClick={onClickModalWriteContinue}
            >
              <Text styleName="body1" color="RG03">
                계속 작성
              </Text>
            </Button>
            <Button
              variant="primary"
              h={40}
              sx={{ width: '100%', marginTop: '40px' }}
              onClick={onClickModalWriteCancel}
            >
              <Text styleName="body1" color="RG00">
                작성 취소
              </Text>
            </Button>
          </ModalButtonDiv>
        }
      />
      <AlertModal
        type="NONE"
        isOpen={isOpenSend}
        messages={
          <Stack sx={{ gap: '8px' }}>
            {watch('isSendNow') ? (
              <Text styleName="body2" color="RG02">
                해당 메시지를 즉시 발송하시겠습니까?
              </Text>
            ) : (
              <Fragment>
                <Text styleName="body2" color="RG02">
                  {dayjs(watch('sendDate')).format('YYYY년 MM월 DD일')} {watch('sendHour')}:{watch('sendMinute')}에
                </Text>
                <Text styleName="body2" color="RG02">
                  메시지를 예약 발송하시겠습니까?
                </Text>
              </Fragment>
            )}
          </Stack>
        }
        buttonGroup={
          <ModalButtonDiv>
            <Button
              variant="tertiary"
              h={40}
              sx={{ width: '100%', marginTop: '40px' }}
              onClick={onClickModalSendCancel}
            >
              <Text styleName="body1" color="RG03">
                {watch('isSendNow') ? '취소' : '닫기'}
              </Text>
            </Button>
            <Button variant="primary" h={40} sx={{ width: '100%', marginTop: '40px' }} onClick={onClickModalSend}>
              <Text styleName="body1" color="RG00">
                {watch('isSendNow') ? '발송하기' : '예약 발송하기'}
              </Text>
            </Button>
          </ModalButtonDiv>
        }
      />
      <RequestErrorModal
        isOpen={isOpenErrorModal}
        setIsOpen={setIsOpenErrorModal}
        errorMessage="메시지 발송에 실패하였습니다."
      />
      <WarningModal
        isOpen={isOpenTimeErrorModal}
        setIsOpen={setIsOpenTimeErrorModal}
        description={
          <>
            <Text styleName="body2" color="RG02">
              이미 지난 날짜/시간으로 예약 발송할 수 없습니다.
            </Text>
          </>
        }
      />
      <WarningModal
        isOpen={isOpen10MinErrorModal}
        setIsOpen={setIsOpen10MinErrorModal}
        description={
          <>
            <Text styleName="body2" color="RG02">
              예약 시간은 현 시간보다 최소 10분 이상으로 설정해야 합니다.
            </Text>
          </>
        }
      />
    </Fragment>
  );
};

export default ManualMessage;

const TitleDiv = styled.div<StackProps>`
  display: flex;
  padding: 20px;
  align-items: center;
  gap: 10px;
  align-self: stretch;

  ${({ theme }) => ({
    borderBottom: `1px solid ${theme.colors.RG06}`,
  })}
`;
const ButtonDiv = styled.div<StackProps>`
  display: flex;
  padding: 20px;
  justify-content: flex-end;
  align-items: center;
  gap: 20px;
  align-self: stretch;

  ${({ theme }) => ({
    borderRadius: `0px 0px 8px 8px`,
    borderTop: `1px solid ${theme.colors.RG06}`,
    background: '#fff',
  })}
`;

const SaveButtonStyle = styled(Button)`
  display: flex;
  width: 216px;
  height: 40px;
  padding: 8px 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;

  ${({ theme, disabled }) => ({
    borderRadius: 8,
    backgroundColor: `${disabled ? theme.colors.RG07 : '#2E66F6'}`,
    borderColor: `${disabled ? theme.colors.RG07 : theme.colors.RG00}`,
  })}
`;
type ButtonTextProps = {
  disable: boolean;
};
const ButtonText = styled.text<ButtonTextProps>`
  ${({ theme, disable }) => ({
    color: `${disable ? theme.colors.RG04 : theme.colors.RG00}`,
  })}
  text-align: center;
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px; /* 150% */
  letter-spacing: -0.32px;
`;
const CancelButtonStyle = styled(Button)`
  display: flex;
  width: 102px;
  height: 40px;
  padding: 8px 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;

  ${({ theme }) => ({
    borderRadius: 8,
    border: `1px solid ${theme.colors.RG06}`,
    backgroundColor: theme.colors.RG00,
  })}
`;

const ModalButtonDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  align-self: stretch;
`;
